import type { RedirectLoginOptions } from "@auth0/auth0-react";

import {
  normalizeOrganizationForAuthorize,
  persistOrganizationLoginChoice,
  resolveAuth0OrganizationForAuthorize,
} from "./authOrganization";
import { nativeOpenUrl } from "./nativeAuth";

/**
 * Auth0 organization invitation parameters carried on the invite-email link
 * (e.g. `?invitation=…&organization=…&organization_name=…`).
 *
 * Only `invitation` + `organization` are forwarded to Auth0; `organization_name`
 * is display-only and must not be sent to the `/authorize` endpoint.
 */
export interface InvitationParams {
  invitation: string;
  organization: string;
}

/**
 * Extract Auth0 invitation parameters from a URL query string. Returns `null`
 * unless both `invitation` and `organization` are present, since Auth0 requires
 * the pair to render the invitation signup flow.
 */
export function readInvitationParams(search: string): InvitationParams | null {
  const params = new URLSearchParams(search);
  const invitation = params.get("invitation");
  const organization = params.get("organization");
  if (invitation && organization) {
    return { invitation, organization };
  }
  return null;
}

const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

/**
 * Build `loginWithRedirect` options. Invitation params (when present) are
 * forwarded so Auth0 shows the invite signup flow. For normal sign-in, omit
 * `organization` so Auth0 Universal Login prompts for the tenant. Pass
 * `organizationOverride` only for in-app org switching (SystemAdmin).
 */
export function buildLoginWithRedirectOptions(
  params: InvitationParams | null,
  returnTo?: string,
  organizationOverride?: string,
): RedirectLoginOptions {
  const organization =
    params?.organization != null
      ? normalizeOrganizationForAuthorize(params.organization)
      : resolveAuth0OrganizationForAuthorize(organizationOverride);

  if (organization) {
    persistOrganizationLoginChoice(organization);
  }

  return {
    appState: {
      returnTo:
        returnTo ??
        (`${window.location.pathname}${window.location.search}` || "/"),
    },
    authorizationParams: {
      ...(audience ? { audience } : {}),
      ...(params
        ? {
            invitation: params.invitation,
            organization: normalizeOrganizationForAuthorize(params.organization),
          }
        : organization
          ? { organization }
          : {}),
    },
    openUrl: nativeOpenUrl(),
  };
}
